'use client'
import type { ReactNode } from 'react'
import { useEffect, useId } from 'react'

export function Dialog({
  open, title, description, width = 480, onClose, footer, children,
}: {
  open: boolean; title: string; description?: string; width?: number
  onClose: () => void; footer?: ReactNode; children: ReactNode
}) {
  const titleId = useId()
  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', background: 'rgba(19,18,17,0.3)', backdropFilter: 'blur(2px)', padding: '1.5rem', paddingTop: '8vh' }}>
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal aria-labelledby={titleId} style={{ width, position: 'relative', maxWidth: '100%' }} className="rounded-sm border border-line-strong bg-surface shadow-[var(--shadow-md)]">
        <header className="flex items-start justify-between gap-6 border-b border-line px-5 py-4">
          <div>
            <h2 id={titleId} className="text-[16px] font-semibold text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-[14px] text-ink-3">{description}</p>}
          </div>
          <button onClick={onClose} aria-label="关闭" className="pxs-focus -mt-0.5 -mr-1 grid h-6 w-6 place-items-center rounded-xs text-ink-3 hover:bg-sunken hover:text-ink">
            <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 fill-none stroke-current"><path d="M1 1l8 8M9 1l-8 8" /></svg>
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-line bg-canvas px-5 py-3">{footer}</footer>}
      </div>
    </div>
  )
}
